import React, { useCallback } from "react";
import classNames from "classnames";
import type { IconProps } from "./types";
import { CommonIcons } from "./types";
import styles from "./Icon.module.css";

// Re-export CommonIcons for external use
export { CommonIcons } from "./types";

/**
 * Icon component - A wrapper for SVG icons.
 * Supports common MDI icons and custom SVG paths.
 * Replaces Vuetify's v-icon component.
 *
 * WCAG AAA Compliant:
 * - Clickable icons are keyboard accessible (Enter/Space)
 * - Proper ARIA roles (img for meaningful, presentation for decorative)
 * - aria-label required for clickable icons
 * - Focus indicator for interactive icons
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  (
    {
      name,
      size = "md",
      color,
      className,
      onClick,
      "aria-label": ariaLabel,
      "aria-hidden": ariaHidden,
      "data-testid": testId,
    },
    ref,
  ) => {
    // Normalize icon name (remove mdi- prefix if present)
    const normalizedName = name.replace(/^mdi-/, "");

    // Get the path from common icons or use name as path directly
    const iconPath =
      CommonIcons[normalizedName as keyof typeof CommonIcons] || null;

    // Determine if icon is interactive
    const isInteractive = !!onClick;

    // Handle keyboard activation for interactive icons (WCAG 2.1.1)
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<SVGSVGElement>) => {
        if (isInteractive && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.(e as unknown as React.MouseEvent<SVGSVGElement>);
        }
      },
      [isInteractive, onClick],
    );

    // Determine ARIA attributes based on usage
    const computedRole = isInteractive
      ? "button"
      : ariaLabel
        ? "img"
        : "presentation";
    const computedAriaHidden = ariaHidden ?? (!ariaLabel && !isInteractive);

    return (
      <svg
        ref={ref}
        className={classNames(
          styles.icon,
          styles[size],
          className,
          isInteractive && styles.clickable,
        )}
        viewBox="0 0 24 24"
        style={color ? { color } : undefined}
        role={computedRole}
        aria-label={ariaLabel}
        aria-hidden={computedAriaHidden}
        onClick={onClick}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        data-testid={testId}
      >
        {iconPath ? (
          <path d={iconPath} fill="currentColor" />
        ) : (
          // Fallback: render a placeholder for unknown icons
          <path
            d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"
            fill="currentColor"
          />
        )}
      </svg>
    );
  },
);

Icon.displayName = "Icon";

export default Icon;
